import { Customer, MetalsLaserMaterial, MetalsPowderCoatingPowder, MetalsPressBrakeBendRadius, MetalsProject, MetalsProjectFile, MetalsTaskType } from "@prism/prism-shared";

export interface TaskValid {
    taskId: string;
    valid: boolean;
    reasons: string[]
}

export enum MetalsTaskStatus {
    Draft = "Draft",
    Waiting = "Waiting",
    Active = "Active",
    Completed = "Completed",
    Canceled = "Canceled",
    Template = 'Template',
}

export const metalsTaskStatusColorDict: Record<MetalsTaskStatus, string> = {
    [MetalsTaskStatus.Active]: 'var(--color-4)',
    [MetalsTaskStatus.Draft]: 'var(--color-2)',
    [MetalsTaskStatus.Waiting]: 'var(--color-3)',
    [MetalsTaskStatus.Completed]: 'var(--color-5)',
    [MetalsTaskStatus.Canceled]: 'var(--color-6)',
    [MetalsTaskStatus.Template]: 'var(--color-2)', //never actually shown
}

export enum MetalsTaskActivationCondition {
    SomeQuantityCompleted = 'SomeQuantityCompleted',
    AllQuantityCompleted = 'AllQuantityCompleted'
}


export interface MetalsTask {
    id: string;
    // code: number;
    type: MetalsTaskType;
    nextTaskId: string | null;
    projectId: string;
    totalQuantity: number;
    completedQuantity: number;
    status: MetalsTaskStatus;
    x: number;
    y: number;
    details: string;
    translatedDetails: string;
    files: MetalsProjectFile[];
    activationCondition: MetalsTaskActivationCondition;
    isHot: boolean;
    hardwareItemIds: string[];
    hardwareItemIdToQtyPerPartDict: Record<string, number>;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}


export interface MetalsTask_Laser extends MetalsTask {
    type: MetalsTaskType.Laser;
    materialId: string;
    approximateWidth: number;
    approximateHeight: number;
}


export interface MetalsTask_PressBrake extends MetalsTask {
    type: MetalsTaskType.PressBrake;
    bendRadiusIds: string[];
}

export interface MetalsTask_PEMs extends MetalsTask {
    type: MetalsTaskType.PEMs;
}

export interface MetalsTask_Fabricating extends MetalsTask {
    type: MetalsTaskType.Fabricating;

}

export interface MetalsTask_PowderCoating extends MetalsTask {
    type: MetalsTaskType.PowderCoating;
    powderId: string;
}

export interface MetalsTask_Stamping extends MetalsTask {
    type: MetalsTaskType.Stamping;
}



export interface MetalsTask_Deburring extends MetalsTask {
    type: MetalsTaskType.Deburring;
}



export interface MetalsTask_ToolRoom extends MetalsTask {
    type: MetalsTaskType.ToolRoom;
}



export interface MetalsTask_TurretPunch extends MetalsTask {
    type: MetalsTaskType.TurretPunch;
}



export interface MetalsTask_RollForming extends MetalsTask {
    type: MetalsTaskType.RollForming;
}



export interface MetalsTask_Assembly extends MetalsTask {
    type: MetalsTaskType.Assembly;
}


export interface MetalsTask_Packaging extends MetalsTask {
    type: MetalsTaskType.Packaging;
}

export interface MetalsTask_DrillPress extends MetalsTask {
    type: MetalsTaskType.DrillPress;
}

export interface MetalsTask_OutsideProcessing extends MetalsTask {
    type: MetalsTaskType.OutsideProcessing;
}

export interface MetalsTask_Transfer extends MetalsTask {
    type: MetalsTaskType.Transfer;
}


export interface DisplayMetalsTask {
    task: MetalsTaskUnion;
    project: MetalsProject;
    customer: Customer;
}


export interface DisplayMetalsTask_Laser extends DisplayMetalsTask {
    task: MetalsTask_Laser;
    material: MetalsLaserMaterial;
    // quantityOnCurrentPrograms: number;
    // quantityOnOtherPrograms: number;
}

export interface DisplayMetalsTask_PressBrake extends DisplayMetalsTask {
    task: MetalsTask_PressBrake;
    bendRadii: MetalsPressBrakeBendRadius[];
}

export interface DisplayMetalsTask_PEMs extends DisplayMetalsTask {
    task: MetalsTask_PEMs;
}

export interface DisplayMetalsTask_Fabricating extends DisplayMetalsTask {
    task: MetalsTask_Fabricating;
}

export interface DisplayMetalsTask_PowderCoating extends DisplayMetalsTask {
    task: MetalsTask_PowderCoating;
    powder: MetalsPowderCoatingPowder;
}

export interface DisplayMetalsTask_Stamping extends DisplayMetalsTask {
    task: MetalsTask_Stamping;
}

export interface DisplayMetalsTask_Deburring extends DisplayMetalsTask {
    task: MetalsTask_Deburring;
}

export interface DisplayMetalsTask_ToolRoom extends DisplayMetalsTask {
    task: MetalsTask_ToolRoom;
}

export interface DisplayMetalsTask_TurretPunch extends DisplayMetalsTask {
    task: MetalsTask_TurretPunch;
}

export interface DisplayMetalsTask_RollForming extends DisplayMetalsTask {
    task: MetalsTask_RollForming;
}

export interface DisplayMetalsTask_Assembly extends DisplayMetalsTask {
    task: MetalsTask_Assembly;
}

export interface DisplayMetalsTask_Packaging extends DisplayMetalsTask {
    task: MetalsTask_Packaging;
}

export interface DisplayMetalsTask_DrillPress extends DisplayMetalsTask {
    task: MetalsTask_DrillPress;
}

export interface DisplayMetalsTask_OutsideProcessing extends DisplayMetalsTask {
    task: MetalsTask_OutsideProcessing;
}

export interface DisplayMetalsTask_Transfer extends DisplayMetalsTask {
    task: MetalsTask_Transfer;
}

export type MetalsTaskUnion =
    | MetalsTask_Laser
    | MetalsTask_PressBrake
    | MetalsTask_PEMs
    | MetalsTask_Fabricating
    | MetalsTask_PowderCoating
    | MetalsTask_Stamping
    | MetalsTask_Deburring
    | MetalsTask_ToolRoom
    | MetalsTask_TurretPunch
    | MetalsTask_RollForming
    | MetalsTask_Assembly
    | MetalsTask_Packaging
    | MetalsTask_DrillPress
    | MetalsTask_OutsideProcessing
    | MetalsTask_Transfer;

export type DisplayMetalsTaskUnion =
    | DisplayMetalsTask_Laser
    | DisplayMetalsTask_PressBrake
    | DisplayMetalsTask_PEMs
    | DisplayMetalsTask_Fabricating
    | DisplayMetalsTask_PowderCoating
    | DisplayMetalsTask_Stamping
    | DisplayMetalsTask_Deburring
    | DisplayMetalsTask_ToolRoom
    | DisplayMetalsTask_TurretPunch
    | DisplayMetalsTask_RollForming
    | DisplayMetalsTask_Assembly
    | DisplayMetalsTask_Packaging
    | DisplayMetalsTask_DrillPress
    | DisplayMetalsTask_OutsideProcessing
    | DisplayMetalsTask_Transfer;

// Then, define GuardMap using a mapped type to preserve the specific predicates.
type GuardMap = {
    [K in MetalsTaskType]: (task: MetalsTask) => task is Extract<MetalsTaskUnion, { type: K }>;
};

type DisplayGuardMap = {
    [K in MetalsTaskType]: (task: DisplayMetalsTaskUnion) => task is Extract<DisplayMetalsTaskUnion, { task: { type: K } }>;
};

export const metalsTaskGuards: GuardMap = {
    [MetalsTaskType.Laser]: (task): task is MetalsTask_Laser => task.type === MetalsTaskType.Laser,
    [MetalsTaskType.PressBrake]: (task): task is MetalsTask_PressBrake => task.type === MetalsTaskType.PressBrake,
    [MetalsTaskType.PEMs]: (task): task is MetalsTask_PEMs => task.type === MetalsTaskType.PEMs,
    [MetalsTaskType.Fabricating]: (task): task is MetalsTask_Fabricating => task.type === MetalsTaskType.Fabricating,
    [MetalsTaskType.PowderCoating]: (task): task is MetalsTask_PowderCoating => task.type === MetalsTaskType.PowderCoating,
    [MetalsTaskType.Stamping]: (task): task is MetalsTask_Stamping => task.type === MetalsTaskType.Stamping,
    [MetalsTaskType.Deburring]: (task): task is MetalsTask_Deburring => task.type === MetalsTaskType.Deburring,
    [MetalsTaskType.ToolRoom]: (task): task is MetalsTask_ToolRoom => task.type === MetalsTaskType.ToolRoom,
    [MetalsTaskType.TurretPunch]: (task): task is MetalsTask_TurretPunch => task.type === MetalsTaskType.TurretPunch,
    [MetalsTaskType.RollForming]: (task): task is MetalsTask_RollForming => task.type === MetalsTaskType.RollForming,
    [MetalsTaskType.Assembly]: (task): task is MetalsTask_Assembly => task.type === MetalsTaskType.Assembly,
    [MetalsTaskType.Packaging]: (task): task is MetalsTask_Packaging => task.type === MetalsTaskType.Packaging,
    [MetalsTaskType.DrillPress]: (task): task is MetalsTask_DrillPress => task.type === MetalsTaskType.DrillPress,
    [MetalsTaskType.OutsideProcessing]: (task): task is MetalsTask_OutsideProcessing => task.type === MetalsTaskType.OutsideProcessing,
    [MetalsTaskType.Transfer]: (task): task is MetalsTask_Transfer => task.type === MetalsTaskType.Transfer,
};

export const displayMetalsTaskGuards: DisplayGuardMap = {
    [MetalsTaskType.Laser]: (task): task is DisplayMetalsTask_Laser => task.task.type === MetalsTaskType.Laser,
    [MetalsTaskType.PressBrake]: (task): task is DisplayMetalsTask_PressBrake => task.task.type === MetalsTaskType.PressBrake,
    [MetalsTaskType.PEMs]: (task): task is DisplayMetalsTask_PEMs => task.task.type === MetalsTaskType.PEMs,
    [MetalsTaskType.Fabricating]: (task): task is DisplayMetalsTask_Fabricating => task.task.type === MetalsTaskType.Fabricating,
    [MetalsTaskType.PowderCoating]: (task): task is DisplayMetalsTask_PowderCoating => task.task.type === MetalsTaskType.PowderCoating,
    [MetalsTaskType.Stamping]: (task): task is DisplayMetalsTask_Stamping => task.task.type === MetalsTaskType.Stamping,
    [MetalsTaskType.Deburring]: (task): task is DisplayMetalsTask_Deburring => task.task.type === MetalsTaskType.Deburring,
    [MetalsTaskType.ToolRoom]: (task): task is DisplayMetalsTask_ToolRoom => task.task.type === MetalsTaskType.ToolRoom,
    [MetalsTaskType.TurretPunch]: (task): task is DisplayMetalsTask_TurretPunch => task.task.type === MetalsTaskType.TurretPunch,
    [MetalsTaskType.RollForming]: (task): task is DisplayMetalsTask_RollForming => task.task.type === MetalsTaskType.RollForming,
    [MetalsTaskType.Assembly]: (task): task is DisplayMetalsTask_Assembly => task.task.type === MetalsTaskType.Assembly,
    [MetalsTaskType.Packaging]: (task): task is DisplayMetalsTask_Packaging => task.task.type === MetalsTaskType.Packaging,
    [MetalsTaskType.DrillPress]: (task): task is DisplayMetalsTask_DrillPress => task.task.type === MetalsTaskType.DrillPress,
    [MetalsTaskType.OutsideProcessing]: (task): task is DisplayMetalsTask_OutsideProcessing => task.task.type === MetalsTaskType.OutsideProcessing,
    [MetalsTaskType.Transfer]: (task): task is DisplayMetalsTask_Transfer => task.task.type === MetalsTaskType.Transfer,
};